import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/kunlun-data';

const categoryImages: Record<string, string> = {
  'glacier-source': 'https://readdy.ai/api/search-image?query=A%20luxurious%20glass%20bottle%20of%20premium%20mineral%20water%20on%20a%20clean%20white%20minimalist%20background%20with%20soft%20diffused%20lighting%2C%20elegant%20product%20photography%2C%20crystal%20clear%20water%20visible%20through%20the%20bottle&width=600&height=400&seq=cat-glacier-01&orientation=landscape',
  'snow-spring': 'https://readdy.ai/api/search-image?query=A%20crystal%20clear%20bottle%20of%20pure%20spring%20water%20standing%20on%20a%20smooth%20river%20stone%20with%20soft%20morning%20sunlight%2C%20natural%20organic%20composition%2C%20fresh%20clean%20minimalist%20product%20photography&width=600&height=400&seq=cat-spring-01&orientation=landscape',
  'limited-edition': 'https://readdy.ai/api/search-image?query=A%20luxurious%20handcrafted%20crystal%20glass%20water%20bottle%20with%20gold%20foil%20accents%20on%20dark%20elegant%20marble%2C%20exclusive%20limited%20edition%20product%20photography%2C%20opulent%20sophisticated%20aesthetic&width=600&height=400&seq=cat-limited-01&orientation=landscape',
};

const productImages: Record<string, string> = {
  'prod-glacier-classic': 'https://readdy.ai/api/search-image?query=A%20single%20elegant%20premium%20mineral%20water%20bottle%20with%20clean%20modern%20label%20design%20isolated%20on%20pure%20white%20background%2C%20soft%20studio%20lighting%2C%20commercial%20product%20photography&width=400&height=300&seq=prod-glacier-classic&orientation=landscape',
  'prod-glacier-premium': 'https://readdy.ai/api/search-image?query=A%20large%20premium%20glass%20water%20bottle%20with%20sophisticated%20label%20design%20on%20white%20background%2C%20elegant%20product%20photography%2C%20crystal%20texture%2C%20luxury%20brand%20feel&width=400&height=300&seq=prod-glacier-premium&orientation=landscape',
  'prod-glacier-mini': 'https://readdy.ai/api/search-image?query=A%20small%20compact%20mineral%20water%20bottle%20with%20minimal%20label%20design%20on%20white%20background%2C%20portable%20size%2C%20clean%20product%20photography%2C%20fresh%20look&width=400&height=300&seq=prod-glacier-mini&orientation=landscape',
  'prod-spring-standard': 'https://readdy.ai/api/search-image?query=A%20standard%20size%20spring%20water%20bottle%20with%20nature%20inspired%20label%20on%20white%20background%2C%20clean%20product%20photography%2C%20refreshing%20look&width=400&height=300&seq=prod-spring-standard&orientation=landscape',
  'prod-spring-family': 'https://readdy.ai/api/search-image?query=A%20large%20family%20size%20water%20bottle%20container%20with%20simple%20label%20on%20white%20background%2C%20practical%20household%20product%20photography&width=400&height=300&seq=prod-spring-family&orientation=landscape',
  'prod-limited-peak': 'https://readdy.ai/api/search-image?query=A%20luxurious%20crystal%20glass%20water%20bottle%20with%20intricate%20gold%20details%20on%20white%20background%2C%20exclusive%20limited%20edition%2C%20opulent%20product%20photography&width=400&height=300&seq=prod-limited-peak&orientation=landscape',
  'prod-limited-collection': 'https://readdy.ai/api/search-image?query=An%20artistic%20handcrafted%20crystal%20water%20bottle%20with%20gold%20foil%20decorations%20on%20white%20background%2C%20collector%20edition%2C%20luxury%20product%20photography&width=400&height=300&seq=prod-limited-collection&orientation=landscape',
};

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '昆仑雪产品系列',
  description: '探索昆仑雪三大产品系列：冰川之源、雪山灵泉、珍藏限量，满足不同消费场景的高端矿泉水需求。',
};

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('series') || categories[0].slug;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  return (
    <>
      <SEO
        title="产品系列"
        description="探索昆仑雪三大产品系列：冰川之源、雪山灵泉、珍藏限量，满足不同消费场景的高端矿泉水需求，每一系列都源自昆仑山万年冰川的纯净馈赠。"
        keywords="昆仑雪产品,冰川之源,雪山灵泉,珍藏限量,高端矿泉水系列,昆仑山矿泉水"
        canonicalUrl="https://kunlunsnow.com/categories"
      />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '产品系列' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5 mb-2">
              产品系列
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              探索昆仑雪的三大产品系列，每一系列都经过精心打造，满足不同消费场景的纯净饮水需求
            </p>
          </div>
        </section>

        <section className="pb-8 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1 w-fit flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setSearchParams({ series: cat.slug })}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 mb-12">
              <div className="lg:w-2/5">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={categoryImages[activeCategory.slug]}
                    alt={`昆仑雪${activeCategory.name}`}
                    title={`昆仑雪${activeCategory.name} - ${activeCategory.subtitle}`}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-3/5">
                <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase font-heading">
                  SERIES
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2 mb-3">
                  {activeCategory.name}
                </h2>
                <p className="text-sm text-foreground-500 mb-2">{activeCategory.subtitle}</p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {activeCategory.description}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {activeCategory.products.map((product) => (
                <div
                  key={product.name}
                  className="bg-background-100 rounded-2xl p-6 hover:bg-background-200/70 transition-colors duration-300"
                >
                  <div className="w-full h-48 rounded-xl overflow-hidden mb-5 bg-background-200">
                    <img
                      src={productImages[product.imageSeq]}
                      alt={product.name}
                      title={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-3 leading-relaxed">
                    {product.description}
                  </p>
                  <p className="font-heading text-lg font-bold text-accent-600 mb-3">
                    {product.price}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((f) => (
                      <span
                        key={f}
                        className="text-[10px] px-2 py-1 bg-secondary-100 text-secondary-700 rounded-md font-medium whitespace-nowrap"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}